import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import WorkflowLogo from './WorkflowLogo';
import { cn } from '../../lib/utils';
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ClipboardIcon,
  GlobeAltIcon,
  LinkIcon,
  PaintBrushIcon,
} from '@heroicons/react/24/outline';

export type WorkflowSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface WorkflowHeaderProps {
  projectName: string;
  onProjectNameChange?: (name: string) => void;
  zoom: number;
  onZoomChange?: (zoom: number) => void;
  /** "Export to Planverse" — publishes publicly to the community feed. */
  onExportToPlanverse?: () => void;
  /** "Publish" — creates a shareable link, but NOT on the Planverse feed. */
  onPublishUnlisted?: () => void;
  /** "Get link" — copies the current URL to the clipboard. */
  onGetLink?: () => void;
  userInitial?: string;
  /** Draft persistence status — shown next to the project name. */
  saveStatus?: WorkflowSaveStatus;
  /** Epoch ms of the last successful save — used to render "5s ago". */
  lastSavedAt?: number | null;
}

const zoomPresets = [25, 50, 75, 100, 150, 200];

function formatSavedAgo(ms: number): string {
  const secs = Math.floor((Date.now() - ms) / 1000);
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

export default function WorkflowHeader({
  projectName,
  onProjectNameChange,
  zoom,
  onZoomChange,
  onExportToPlanverse,
  onPublishUnlisted,
  onGetLink,
  userInitial = 'T',
  saveStatus = 'idle',
  lastSavedAt = null,
}: WorkflowHeaderProps) {
  const navigate = useNavigate();
  const [zoomOpen, setZoomOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(projectName);
  const [editing, setEditing] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setNameDraft(projectName);
  }, [projectName]);

  // Ticker so the "Saved 5s ago" label updates while the header is mounted.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setNowTick((n) => n + 1), 10000);
    return () => clearInterval(interval);
  }, []);
  void nowTick;

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) setShareOpen(false);
      if (zoomRef.current && !zoomRef.current.contains(e.target as Node)) setZoomOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commitName = () => {
    setEditing(false);
    if (nameDraft.trim() && nameDraft !== projectName) onProjectNameChange?.(nameDraft.trim());
    else setNameDraft(projectName);
  };

  return (
    <header className="flex h-14 w-full flex-shrink-0 items-center justify-between border-b border-border bg-background font-display">
      <div className="flex items-center gap-4">
        <Link
          to="/orbi"
          className="flex h-14 items-center justify-center border-r border-border px-4 transition-colors hover:bg-muted"
          aria-label="Back to Orbi"
        >
          <WorkflowLogo size={20} />
        </Link>
        {editing ? (
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') { setNameDraft(projectName); setEditing(false); }
            }}
            aria-label="Project name"
            className="border-0 bg-transparent text-base text-foreground focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-base text-foreground hover:opacity-80"
          >
            {projectName}
          </button>
        )}
        {/* Save status — subtle, next to the project name. */}
        <span className="hidden sm:inline text-xs text-muted-foreground" aria-live="polite">
          {saveStatus === 'saving' && '· Saving…'}
          {saveStatus === 'saved' && lastSavedAt && `· Saved ${formatSavedAgo(lastSavedAt)}`}
          {saveStatus === 'error' && <span className="text-destructive">· Save failed</span>}
        </span>
      </div>

      <div className="flex items-center gap-3 pr-4">
        <div className="relative" ref={zoomRef}>
          <button
            type="button"
            onClick={() => setZoomOpen((v) => !v)}
            className="flex items-center gap-1 rounded px-2 py-1 text-sm text-foreground hover:bg-muted"
            aria-expanded={zoomOpen}
          >
            <span>{Math.round(zoom * 100)}%</span>
            <ChevronDownIcon className="h-4 w-4" />
          </button>
          {zoomOpen && (
            <div className="absolute right-0 top-full z-20 mt-1 flex w-24 flex-col overflow-hidden rounded-md border border-border bg-background shadow-md">
              {zoomPresets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    onZoomChange?.(p / 100);
                    setZoomOpen(false);
                  }}
                  className={cn(
                    'px-3 py-1.5 text-left text-sm hover:bg-muted',
                    Math.round(zoom * 100) === p && 'text-primary',
                  )}
                >
                  {p}%
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Share dropdown */}
        <div className="relative" ref={shareRef}>
          <button
            type="button"
            onClick={() => setShareOpen((v) => !v)}
            className="flex items-center gap-1 rounded-full bg-primary pl-4 pr-3 py-1.5 text-sm text-primary-foreground transition-opacity hover:opacity-90"
            aria-expanded={shareOpen}
          >
            <span>Share</span>
            <ChevronDownIcon className="h-4 w-4" />
          </button>
          {shareOpen && (
            <div className="absolute right-0 top-full z-20 mt-2 flex w-64 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-md">
              <ShareItem
                icon={GlobeAltIcon}
                label="Export to Planverse"
                hint="Public — appears on the Planverse feed"
                onClick={() => { onExportToPlanverse?.(); setShareOpen(false); }}
              />
              <ShareItem
                icon={ArrowTopRightOnSquareIcon}
                label="Publish"
                hint="Link-only — not on the feed"
                onClick={() => { onPublishUnlisted?.(); setShareOpen(false); }}
              />
              <ShareItem
                icon={LinkIcon}
                label="Get link"
                hint="Copy the current workflow link"
                onClick={() => { onGetLink?.(); setShareOpen(false); }}
              />
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="Theme"
          className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <PaintBrushIcon className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => navigate('/profile')}
          aria-label="Profile"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border text-xs text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#f79878', borderColor: '#f47950' }}
        >
          {userInitial}
        </button>
      </div>
    </header>
  );
}

function ShareItem({
  icon: Icon,
  label,
  hint,
  onClick,
}: {
  icon: typeof ClipboardIcon;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted"
    >
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </div>
    </button>
  );
}
